import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import api from '../../config/api';
import {
  adminOutlineBtnSx,
  adminSectionTitleSx,
  adminStatPaperSx,
  adminSurfaceCardSx,
} from '../../components/AdminChrome';
import { DOCUMENT } from '../../constants/documentTheme';

export default function PrivatePerformancePanel({ t }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/private/reports');
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const exportJson = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dvn-private-kpis-${(report.generatedAt || '').slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  const metrics = report?.metrics || {};
  const funnel = report?.funnel || {};

  return (
    <Box>
      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert> : null}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button startIcon={<RefreshIcon />} sx={adminOutlineBtnSx} onClick={load}>{t('adminPrivate.refresh')}</Button>
        <Button sx={adminOutlineBtnSx} onClick={exportJson} disabled={!report}>{t('adminPrivate.exportKpis')}</Button>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('adminPrivate.kpi.gmv'), value: metrics.grossBookingValueDisplay },
          { label: t('adminPrivate.kpi.dvnFeeInvoiced'), value: metrics.dvnFeeInvoicedDisplay },
          { label: t('adminPrivate.kpi.dvnFeeCollected'), value: metrics.dvnFeeCollectedDisplay },
          { label: t('adminPrivate.kpi.bookedBySupplier'), value: funnel.bookedBySupplier ?? 0 },
        ].map((card) => (
          <Grid item xs={6} md={3} key={card.label}>
            <Card sx={adminStatPaperSx} elevation={0}>
              <Typography sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 1 }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: '1.35rem' }}>{card.value}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Card sx={{ ...adminSurfaceCardSx, p: 2.5 }} elevation={0}>
        <Typography sx={adminSectionTitleSx}>{t('adminPrivate.funnelTitle')}</Typography>
        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2 }}>
          {t('adminPrivate.funnelHint')}
        </Typography>
        <pre style={{ fontFamily: DOCUMENT.sans, fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(funnel, null, 2)}
        </pre>
      </Card>
    </Box>
  );
}
